import { useCallback, useEffect, useMemo } from 'react'

import { useAppointmentSchedule } from './useAppointmentSchedule'
import { useRoomAttachment } from './useRoomAttachment'
import { useRoomData } from './useRoomData'
import { useTaskpaneSession } from './useTaskpaneSession'
import { useTaskpaneUiState } from './useTaskpaneUiState'

export function useRoomsTaskpane() {
    const session = useTaskpaneSession()
    const ui = useTaskpaneUiState()
    const appointment = useAppointmentSchedule(session.status === 'ready')
    const { initializeSelectedOffice, retainValidTagIds } = ui
    const handleTagsLoaded = useCallback(
        (tags: Array<{ id: string }>) => {
            retainValidTagIds(new Set(tags.map((tag) => tag.id)))
        },
        [retainValidTagIds],
    )

    useEffect(() => {
        if (session.initialOfficeId) {
            initializeSelectedOffice(session.initialOfficeId)
        }
    }, [initializeSelectedOffice, session.initialOfficeId])

    const roomData = useRoomData({
        enabled: session.status === 'ready',
        sessionToken: session.sessionToken,
        sessionClaims: session.sessionClaims,
        selectedOfficeId: ui.selectedOfficeId,
        microsoftTenantId: session.microsoftTenantId,
        currentEventDate: appointment.currentEventDate,
        appointmentStart: appointment.appointmentStart,
        appointmentEnd: appointment.appointmentEnd,
        searchTerm: ui.searchTerm,
        activeTagIds: ui.activeTagIds,
        minCapacity: ui.minCapacity,
        onTagsLoaded: handleTagsLoaded,
    })

    const roomAttachment = useRoomAttachment({
        enabled: session.status === 'ready',
        allMappedRooms: roomData.allMappedRooms,
        roomAvailabilityById: roomData.roomAvailabilityById,
    })

    const selectedOffice = useMemo(
        () => session.offices.find((office) => office.id === ui.selectedOfficeId) || null,
        [session.offices, ui.selectedOfficeId],
    )

    const capacityOptions = useMemo(
        () =>
            Array.from(
                new Set(
                    roomData.allMappedRooms
                        .map((room) => room.capacity)
                        .filter((capacity): capacity is number => Number.isFinite(capacity) && capacity > 0),
                ),
            ).sort((left, right) => left - right),
        [roomData.allMappedRooms],
    )

    return {
        status: session.status,
        authMessage: session.authMessage,
        offices: session.offices,
        selectedOffice,
        selectedOfficeId: ui.selectedOfficeId,
        searchTerm: ui.searchTerm,
        isOfficeOpen: ui.isOfficeOpen,
        isFilterOpen: ui.isFilterOpen,
        availableTags: roomData.availableTags,
        activeTagIds: ui.activeTagIds,
        minCapacity: ui.minCapacity,
        capacityOptions,
        filteredGroups: roomData.filteredGroups,
        roomAvailabilityById: roomData.roomAvailabilityById,
        roomsLoadingMessage: roomData.roomsLoadingMessage,
        roomsLoaded: roomData.roomsLoaded,
        attachedRoomId: roomAttachment.attachedRoomId,
        attachError: roomAttachment.attachError,
        attachingRoomId: roomAttachment.attachingRoomId,
        startTimeLabel: appointment.startTimeLabel,
        endTimeLabel: appointment.endTimeLabel,
        officeSelectRef: ui.officeSelectRef,
        filterPanelRef: ui.filterPanelRef,
        filterTriggerRef: ui.filterTriggerRef,
        setSearchTerm: ui.setSearchTerm,
        setMinCapacity: ui.setMinCapacity,
        retry: session.retry,
        toggleOfficeOpen: ui.toggleOfficeOpen,
        toggleFilterOpen: ui.toggleFilterOpen,
        selectOffice: ui.selectOffice,
        toggleTag: ui.toggleTag,
        clearTags: ui.clearTags,
        toggleGroup: roomData.toggleGroup,
        attachMappedRoomToAppointment: roomAttachment.attachMappedRoomToAppointment,
    }
}
